import React, { useCallback, useMemo, useState } from 'react';

import { Collapse, IconButton } from '@mui/material';
import clsx from 'clsx';
import { ChevronRight, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { AlineAiAvatar, AlineAvatar, AlineTooltip } from 'src/components';
import { Apollo } from 'src/gql/generated';
import type { Apollo as ApolloTypes } from 'src/gql/generated';
import { getPermissionAssigneeDisplayDetails, getPermissionUser } from 'src/helpers';
import { useGlobalDrawer } from 'src/layouts/GlobalRightDrawer';
import { makeStyles } from 'tss-react/mui';

import { AddTaskInput } from './AddTaskInput';
import { EmptyTaskList } from './EmptyTaskList';
import {
  ALINE_AI_KEY,
  getNewTaskGroupKey,
  groupPhaseTasks,
  TASK_GROUP_BY,
} from './phase-task-groups';
import type { TaskGroup, TaskGroupBy } from './phase-task-groups';
import { PhasesHeader } from './PhasesHeader';
import type { PhaseAddOptions } from './PhasesHeader';
import { PhaseTaskRowWithSubtasks } from './PhaseTaskRowWithSubtasks';
import { TaskGroupByMenu } from './TaskGroupByMenu';
import {
  StartWorkflowAfterTaskDialog,
  useStartTask,
} from '../side-panel/task-review/use-start-task';
import type { StartWorkflowMeta } from '../side-panel/task-review/use-start-task';
import type { ParkedWorkflowMeta } from '../use-parked-workflow-completion';
import { useWorkflowDetailLinks } from '../WorkflowDetailContext';

const TOP_COMPOSER_KEY = 'top';

type PhaselessWorkflow = ParkedWorkflowMeta & Pick<StartWorkflowMeta, 'boardId' | 'phases'>;

type PhaseTask = ApolloTypes.PhaseSimple_NonSigningPhase_Fragment['tasks'][number];

const useStyles = makeStyles()(theme => ({
  chevron: {
    color: theme.appColors.iconsType,
    flexShrink: 0,
    transition: 'transform 120ms ease',
  },
  chevronOpen: {
    transform: 'rotate(90deg)',
  },
  /** Lines the typed title up with task titles, past the rows' completion circle. */
  composer: {
    paddingLeft: theme.spacing(4.5),
  },
  group: {
    display: 'flex',
    flexDirection: 'column',
  },
  groupAdd: {
    color: theme.appColors.iconsType,
    marginRight: theme.spacing(0.5),
    padding: theme.spacing(0.5),
  },
  groupCount: {
    color: theme.palette.text.secondary,
    fontSize: 14,
  },
  groupHeader: {
    alignItems: 'center',
    backgroundColor: theme.appColors.darkBackground,
    borderRadius: 6,
    display: 'flex',
    minHeight: 36,
  },
  groupLabel: {
    color: theme.appColors.iconsType,
    fontSize: 14,
    fontWeight: 600,
  },
  /** Fills the whole header, so every spot outside the add button toggles the group. */
  groupToggle: {
    alignItems: 'center',
    alignSelf: 'stretch',
    background: 'none',
    border: 'none',
    borderRadius: 'inherit',
    cursor: 'pointer',
    display: 'flex',
    flex: 1,
    gap: theme.spacing(1),
    minWidth: 0,
    padding: theme.spacing(0, 1),
    textAlign: 'left',
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    width: '100%',
  },
}));

interface TaskGroupSectionProps {
  collapsed: boolean;
  composerOpen: boolean;
  group: TaskGroup<PhaseTask>;
  // The heading names the status, so top-level rows drop their status chip.
  isStatusGroup: boolean;
  onCloseComposer: () => void;
  onOpenComposer: (key: string) => void;
  onTaskCreated: (task: ApolloTypes.TaskSimpleFragment) => void;
  onToggle: (key: string) => void;
  phase: ApolloTypes.PhaseSimple_NonSigningPhase_Fragment;
  readOnly: boolean;
  showHeader: boolean;
  workflow: PhaselessWorkflow;
}

const TaskGroupSection = React.memo(function TaskGroupSection({
  collapsed,
  composerOpen,
  group,
  isStatusGroup,
  onCloseComposer,
  onOpenComposer,
  onTaskCreated,
  onToggle,
  phase,
  readOnly,
  showHeader,
  workflow,
}: TaskGroupSectionProps) {
  const { classes } = useStyles();
  const canAdd = !readOnly && Boolean(group.newTaskAssignees);
  const expanded = !showHeader || !collapsed;
  const starter = useStartTask(workflow);
  const { offerWorkflowStart } = starter;
  const handleCreated = useCallback(
    (task: ApolloTypes.TaskSimpleFragment) => {
      if (group.startsNewTask) offerWorkflowStart();
      onTaskCreated(task);
    },
    [group.startsNewTask, offerWorkflowStart, onTaskCreated]
  );

  return (
    <div className={classes.group}>
      {showHeader && (
        <div className={classes.groupHeader}>
          <button
            aria-expanded={expanded}
            className={classes.groupToggle}
            type="button"
            onClick={() => onToggle(group.key)}
          >
            <ChevronRight
              className={clsx(classes.chevron, expanded && classes.chevronOpen)}
              size={14}
            />
            <GroupAvatar group={group} />
            <span className={classes.groupLabel}>{group.label}</span>
            <span className={classes.groupCount}>{group.rows.length}</span>
          </button>
          {canAdd && (
            <AlineTooltip arrow placement="top" title={`Add a task to ${group.label}`}>
              <IconButton
                aria-label={`Add a task to ${group.label}`}
                className={classes.groupAdd}
                size="small"
                onClick={() => onOpenComposer(group.key)}
              >
                <Plus size={16} />
              </IconButton>
            </AlineTooltip>
          )}
        </div>
      )}
      <Collapse unmountOnExit in={expanded}>
        {group.rows.map(row => (
          <PhaseTaskRowWithSubtasks
            key={row.task.id}
            hideStatus={isStatusGroup}
            phase={phase}
            readOnly={readOnly}
            row={row}
            workflow={workflow}
          />
        ))}
      </Collapse>
      {canAdd && composerOpen && (
        <div className={classes.composer}>
          <AddTaskInput
            autoFocus
            assignees={group.newTaskAssignees ?? undefined}
            phaseId={phase.id}
            start={group.startsNewTask}
            workflowId={workflow.id}
            onCreated={handleCreated}
            onDismiss={onCloseComposer}
          />
        </div>
      )}
      <StartWorkflowAfterTaskDialog
        open={starter.isPromptOpen}
        workflow={workflow}
        onClose={starter.dismissPrompt}
      />
    </div>
  );
});

interface PhaselessTaskListProps {
  addOptions: PhaseAddOptions;
  /** The workflow's only phase; null until the first task creates it. */
  phase: ApolloTypes.PhaseSimple_NonSigningPhase_Fragment | null;
  readOnly: boolean;
  workflow: PhaselessWorkflow;
  /** Rendered between the header and the list, such as the intake form. */
  children?: React.ReactNode;
}

/**
 * A workflow's tasks when it has at most one task phase, without phase
 * chrome. Nothing starts on its own here; a task is started from its drawer.
 */
export const PhaselessTaskList = React.memo(function PhaselessTaskList({
  addOptions,
  children,
  phase,
  readOnly,
  workflow,
}: PhaselessTaskListProps) {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const { openDrawer } = useGlobalDrawer();
  const links = useWorkflowDetailLinks(workflow.id);
  const isTemplate = workflow.variant === Apollo.WorkflowVariant.Template;
  const [groupBy, setGroupBy] = useState<TaskGroupBy>(TASK_GROUP_BY.Status);
  const [collapsedKeys, setCollapsedKeys] = useState<ReadonlySet<string>>(new Set());
  const [composerKey, setComposerKey] = useState<string | null>(null);
  const tasks = useMemo(() => phase?.tasks ?? [], [phase]);
  const groups = useMemo(
    () => groupPhaseTasks({ groupBy, isTemplate, openGroupKey: composerKey, tasks }),
    [composerKey, groupBy, isTemplate, tasks]
  );
  const showTopComposer = !readOnly && !tasks.length;

  const handleTaskCreated = useCallback(
    (task: ApolloTypes.TaskSimpleFragment) => {
      setComposerKey(null);
      openDrawer();
      navigate(links.task(task.id));
    },
    [links, navigate, openDrawer]
  );
  const closeComposer = useCallback(() => setComposerKey(null), []);
  const openComposer = useCallback((key: string) => {
    setComposerKey(key);
    setCollapsedKeys(previous => withoutKey(previous, key));
  }, []);
  const openHeaderComposer = useCallback(() => {
    if (!tasks.length) setComposerKey(TOP_COMPOSER_KEY);
    else openComposer(getNewTaskGroupKey({ groupBy, isTemplate }));
  }, [groupBy, isTemplate, openComposer, tasks.length]);
  const changeGroupBy = useCallback((value: TaskGroupBy) => {
    setGroupBy(value);
    setComposerKey(null);
  }, []);
  const toggleGroup = useCallback(
    (key: string) =>
      setCollapsedKeys(previous =>
        previous.has(key) ? withoutKey(previous, key) : new Set([...previous, key])
      ),
    []
  );

  return (
    <>
      <PhasesHeader
        actions={tasks.length > 0 && <TaskGroupByMenu value={groupBy} onChange={changeGroupBy} />}
        addOptions={addOptions}
        aiIsGenerating={readOnly}
        title="Tasks"
        onAddTask={openHeaderComposer}
      />
      {children}
      {(showTopComposer || groups.length > 0) && (
        <div className={classes.list}>
          {showTopComposer && (
            <EmptyTaskList
              autoFocus={composerKey === TOP_COMPOSER_KEY}
              canAddSigning={!addOptions.hasSigningPhase}
              isTemplate={isTemplate}
              phaseId={phase?.id ?? null}
              workflowId={workflow.id}
              onCreated={handleTaskCreated}
              onDismiss={closeComposer}
              onPhaseCreated={addOptions.onPhaseCreated}
            />
          )}
          {phase &&
            groups.map(group => (
              <TaskGroupSection
                key={group.key}
                collapsed={collapsedKeys.has(group.key)}
                composerOpen={composerKey === group.key}
                group={group}
                isStatusGroup={groupBy === TASK_GROUP_BY.Status}
                phase={phase}
                readOnly={readOnly}
                showHeader={groupBy !== TASK_GROUP_BY.None}
                workflow={workflow}
                onCloseComposer={closeComposer}
                onOpenComposer={openComposer}
                onTaskCreated={handleTaskCreated}
                onToggle={toggleGroup}
              />
            ))}
        </div>
      )}
    </>
  );
});

/** The person or role an assignee group belongs to; nothing for status groups. */
function GroupAvatar({ group }: { group: TaskGroup<PhaseTask> }) {
  if (group.key === ALINE_AI_KEY) return <AlineAiAvatar />;
  const permission = group.rows
    .at(0)
    ?.task.assignments.find(assignment => assignment.permission.id === group.key)?.permission;
  if (!permission) return null;
  return (
    <AlineAvatar
      avatarInfo={getPermissionUser(permission) ?? permission.role}
      styles={{
        backgroundColor: getPermissionAssigneeDisplayDetails(permission).avatarColor,
        size: 'sm',
      }}
    />
  );
}

function withoutKey(keys: ReadonlySet<string>, key: string): ReadonlySet<string> {
  if (!keys.has(key)) return keys;
  return new Set([...keys].filter(existing => existing !== key));
}
